/**
 * @fileoverview ReactSession Component - Proctoring Session Viewer
 * 
 * A comprehensive session viewer component for live and recorded proctoring sessions.
 * Handles LiveKit media connections, session details, chat, annotations, and more.
 * 
 * @module shared/components/proctoring/SessionViewer
 */

import React from 'react';
import { StoreProvider } from '../../stores/StoreContext';
import { ReactSession as Session } from './Session';
import PropTypes from 'prop-types';
import config from '../../config';

import '@mdxeditor/editor/style.css'

/**
 * ReactSession Component
 * 
 * Wrapper component that provides store context for the Session viewer.
 * This component handles all aspects of viewing a proctoring session including:
 * - Live video/audio streaming via LiveKit
 * - Recorded session playback
 * - Session details and metadata
 * - Chat functionality
 * - Annotations and flagging
 * - Connection quality monitoring
 * 
 * @component
 * @param {ReactSessionProps} props - Component props
 * @returns {React.ReactElement} The session viewer component
 * 
 * @example
 * // Basic live session
 * <ReactSession
 *   sessionSid="session_123"
 *   installSid="install_456"
 *   examSid="exam_789"
 *   apiUrl="https://api.example.com"
 *   roomId="room_123"
 *   spToken="your_jwt_token"
 *   identity="proctor_001"
 *   websocketUrl="wss://ws.example.com"
 *   type="proctor"
 * />
 * 
 * @example
 * // With callbacks and custom components
 * <ReactSession
 *   sessionSid="session_123"
 *   installSid="install_456"
 *   examSid="exam_789"
 *   apiUrl="https://api.example.com"
 *   roomId="room_123"
 *   spToken="your_jwt_token"
 *   identity="proctor_001"
 *   websocketUrl="wss://ws.example.com"
 *   type="proctor"
 *   enabledComponents={{
 *     sessionDetails: true,
 *     sessionHeader: true,
 *     microphone: false,
 *     camera: false,
 *     chat: true,
 *     screenShare: false,
 *     settings: true,
 *     leave: false
 *   }}
 *   video={{
 *     sources: ['live', 'recorded'],
 *     default: 'live'
 *   }}
 *   onSessionLoaded={(data) => console.log('Session loaded', data)}
 *   onConnected={() => console.log('Connected to LiveKit')}
 *   onDisconnected={() => console.log('Disconnected from LiveKit')}
 *   onUnreadMessageCount={(count) => console.log('Unread messages:', count)}
 *   onFlaggingChange={(flags) => console.log('Flags changed:', flags)}
 *   onSessionEnded={({ reason, description }) => console.log('Session ended:', reason, description)}
 *   ref={sessionViewerRef}
 * />
 */
export const ReactSession = React.forwardRef((props, ref) => {
  const { settings, ...sessionProps } = props;

  return (
    <div style={{
      all: 'initial',
      display: 'block',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      fontFamily: 'inherit',
      fontSize: 'inherit',
      lineHeight: 'inherit',
      color: 'inherit'
    }}>
      <StoreProvider initialState={{ settings }}>
        <Session {...sessionProps} ref={ref} />
      </StoreProvider>
    </div>
  );
});

/**
 * @typedef {Object} ReactSessionProps
 * 
 * @property {string} apiUrl - Base URL for the proctoring API
 * @property {string} roomId - LiveKit room identifier
 * @property {string} spToken - JWT authentication token for API access
 * @property {string} sessionSid - Unique session identifier
 * @property {string} examSid - Exam identifier
 * @property {string} installSid - Installation identifier
 * @property {string} identity - User identity for LiveKit connection
 * @property {string} websocketUrl - WebSocket URL for LiveKit media server
 * @property {EnabledComponents} [enabledComponents] - Configuration for which UI components to show
 * @property {VideoConfig} [video] - Video source configuration
 * @property {MagnificationConfig} [magnification] - Video magnification configuration
 * @property {SettingsConfig} [settings] - Initial settings configuration (audio devices, etc.)
 * @property {('proctor'|'student')} type - User type (determines permissions and UI)
 * @property {('live'|'recorded')} [viewMode] - Session viewing mode
 * 
 * @property {Function} [onSessionLoaded] - Callback when session fully loads
 * @property {Function} [onSessionApproved] - Callback when session is approved
 * @property {Function} [onSessionDenied] - Callback when session is denied
 * @property {Function} [onSessionEnded] - Callback when session is ended
 * 
 * @property {Function} [onConnected] - Callback when LiveKit connection is established
 * @property {Function} [onDisconnected] - Callback when LiveKit connection is lost
 * @property {Function} [onConnectionError] - Callback when connection fails
 * @property {Function} [onMediaDeviceFailure] - Callback when media device fails
 * @property {Function} [onEncryptionError] - Callback when encryption error occurs
 * @property {Function} [onOffline] - Callback when internet connection goes offline
 * @property {Function} [onOnline] - Callback when internet connection comes online
 * 
 * @property {Function} [onMicrophoneShareChange] - Callback when microphone share state changes
 * @property {Function} [onCameraShareChange] - Callback when camera share state changes
 * @property {Function} [onScreenShareChange] - Callback when screen share state changes
 * 
 * @property {Function} [onRoomReconnecting] - Callback when room is reconnecting
 * @property {Function} [onRoomReconnected] - Callback when room reconnects successfully
 * @property {Function} [onIsRecordingChanged] - Callback when recording status changes
 * @property {Function} [onRoomDisconnected] - Callback when room disconnects
 * @property {Function} [onRoomMetadataChanged] - Callback when room metadata changes
 * @property {Function} [onRoomMetricsReceived] - Callback when room metrics are received
 * 
 * @property {Function} [onRemoteParticipantConnected] - Callback when remote participant joins
 * @property {Function} [onRemoteParticipantDisconnected] - Callback when remote participant leaves
 * @property {Function} [onRemoteTrackPublished] - Callback when remote track is published
 * @property {Function} [onRemoteTrackUnpublished] - Callback when remote track is unpublished
 * @property {Function} [onRemoteTrackMuted] - Callback when remote track is muted
 * @property {Function} [onRemoteTrackUnmuted] - Callback when remote track is unmuted
 * @property {Function} [onRemoteConnectionQualityChanged] - Callback when remote connection quality changes
 * @property {Function} [onRemoteConnectionStateChanged] - Callback when remote connection state changes
 * @property {Function} [onRemoteParticipantAttributesChanged] - Callback when remote participant attributes change
 * @property {Function} [onRemoteParticipantMetadataChanged] - Callback when remote participant metadata changes
 * @property {Function} [onRemoteTrackStreamStateChanged] - Callback when remote track stream state changes
 * 
 * @property {Function} [onLocalTrackPublished] - Callback when local track is published
 * @property {Function} [onLocalTrackUnpublished] - Callback when local track is unpublished
 * @property {Function} [onLocalTrackMuted] - Callback when local track is muted
 * @property {Function} [onLocalTrackUnmuted] - Callback when local track is unmuted
 * @property {Function} [onRemoteTrackSubscribed] - Callback when subscribed to remote track
 * @property {Function} [onRemoteTrackUnsubscribed] - Callback when unsubscribed from remote track
 * @property {Function} [onRemoteTrackSubscriptionFailed] - Callback when track subscription fails
 * @property {Function} [onLocalConnectionQualityChanged] - Callback when local connection quality changes
 * @property {Function} [onLocalConnectionStateChanged] - Callback when local connection state changes
 * @property {Function} [onLocalParticipantAttributesChanged] - Callback when local participant attributes change
 * @property {Function} [onLocalParticipantMetadataChanged] - Callback when local participant metadata changes
 * @property {Function} [onLocalTrackStreamStateChanged] - Callback when local track stream state changes
 * 
 * @property {Function} [onUnreadMessageCount] - Callback when unread message count changes
 * @property {Function} [onFlaggingChange] - Callback when flagging status changes
 * @property {Function} [onSettingsChange] - Callback when settings are changed (receives changed settings object)
 */

/**
 * @typedef {Object} EnabledComponents
 * Configuration for which UI components to display
 * 
 * @property {boolean} [sessionDetails=true] - Show session details panel
 * @property {boolean} [sessionHeader=true] - Show session header
 * @property {boolean} [allowFullScreen=true] - Allow full screen mode
 * @property {boolean} [microphone=false] - Show microphone controls
 * @property {boolean} [camera=false] - Show camera controls
 * @property {boolean} [chat=false] - Show chat interface
 * @property {boolean} [screenShare=false] - Show screen share controls
 * @property {boolean} [settings=false] - Show settings panel
 * @property {boolean} [leave=false] - Show leave button
 */

/**
 * @typedef {Object} VideoConfig
 * Configuration for video sources
 * 
 * @property {Array<'live'|'recorded'>} sources - Available video sources
 * @property {'live'|'recorded'} default - Default video source to use
 */

/**
 * @typedef {Object} MagnificationConfig
 * Configuration for video magnification (hover-to-zoom)
 * 
 * @property {boolean} enabled - Whether magnification is enabled
 * @property {number} [level=2] - Magnification level (e.g., 2 = 2x zoom)
 * @property {number} [lensSize=200] - Size of magnification lens in pixels
 */

/**
 * @typedef {Object} SettingsConfig
 * Initial settings configuration for the SessionViewer
 * 
 * @property {AudioSettings} [audio] - Audio device settings
 */

/**
 * @typedef {Object} AudioSettings
 * Audio device configuration
 * 
 * @property {AudioDevice} [input] - Audio input (microphone) device
 * @property {AudioDevice} [output] - Audio output (speaker) device
 */

/**
 * @typedef {Object} AudioDevice
 * Audio device specification
 * 
 * @property {string} deviceId - Device ID (use "default" for system default)
 * @property {string} label - Human-readable device name
 */

ReactSession.propTypes = {
  // Required props
  apiUrl: PropTypes.string.isRequired,
  roomId: PropTypes.string.isRequired,
  spToken: PropTypes.string.isRequired,
  sessionSid: PropTypes.string.isRequired,
  examSid: PropTypes.string.isRequired,
  installSid: PropTypes.string.isRequired,
  identity: PropTypes.string.isRequired,
  websocketUrl: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['proctor', 'student']).isRequired,

  // Optional props
  viewMode: PropTypes.oneOf(['live', 'recorded']),
  enabledComponents: PropTypes.shape({
    sessionDetails: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.shape({
        defaultTab: PropTypes.string,
        tabs: PropTypes.arrayOf(
          PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
              id: PropTypes.string.isRequired,
              title: PropTypes.string.isRequired,
              url: PropTypes.string.isRequired,
            }),
          ])
        ),
      }),
    ]),
    sessionHeader: PropTypes.bool,
    allowFullScreen: PropTypes.bool,
    microphone: PropTypes.bool,
    camera: PropTypes.bool,
    chat: PropTypes.bool,
    screenShare: PropTypes.bool,
    settings: PropTypes.bool,
    leave: PropTypes.bool,
  }),
  video: PropTypes.shape({
    sources: PropTypes.arrayOf(PropTypes.oneOf(['live', 'recorded'])),
    default: PropTypes.oneOf(['live', 'recorded']),
  }),
  settings: PropTypes.shape({
    audio: PropTypes.shape({
      input: PropTypes.shape({
        deviceId: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      }),
      output: PropTypes.shape({
        deviceId: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      }),
    }),
  }),

  // Session callbacks
  onSessionLoaded: PropTypes.func,
  onSessionApproved: PropTypes.func,
  onSessionDenied: PropTypes.func,

  // Connection callbacks
  onConnected: PropTypes.func,
  onDisconnected: PropTypes.func,
  onConnectionError: PropTypes.func,
  onMediaDeviceFailure: PropTypes.func,
  onEncryptionError: PropTypes.func,
  onOffline: PropTypes.func,
  onOnline: PropTypes.func,

  // Media share callbacks
  onMicrophoneShareChange: PropTypes.func,
  onCameraShareChange: PropTypes.func,
  onScreenShareChange: PropTypes.func,

  // Room callbacks
  onRoomReconnecting: PropTypes.func,
  onRoomReconnected: PropTypes.func,
  onIsRecordingChanged: PropTypes.func,
  onRoomDisconnected: PropTypes.func,
  onRoomMetadataChanged: PropTypes.func,
  onRoomMetricsReceived: PropTypes.func,

  // Remote participant callbacks
  onRemoteParticipantConnected: PropTypes.func,
  onRemoteParticipantDisconnected: PropTypes.func,
  onRemoteTrackPublished: PropTypes.func,
  onRemoteTrackUnpublished: PropTypes.func,
  onRemoteTrackMuted: PropTypes.func,
  onRemoteTrackUnmuted: PropTypes.func,
  onRemoteConnectionQualityChanged: PropTypes.func,
  onRemoteConnectionStateChanged: PropTypes.func,
  onRemoteParticipantAttributesChanged: PropTypes.func,
  onRemoteParticipantMetadataChanged: PropTypes.func,
  onRemoteTrackStreamStateChanged: PropTypes.func,

  // Local participant callbacks
  onLocalTrackPublished: PropTypes.func,
  onLocalTrackUnpublished: PropTypes.func,
  onLocalTrackMuted: PropTypes.func,
  onLocalTrackUnmuted: PropTypes.func,
  onRemoteTrackSubscribed: PropTypes.func,
  onRemoteTrackUnsubscribed: PropTypes.func,
  onRemoteTrackSubscriptionFailed: PropTypes.func,
  onLocalConnectionQualityChanged: PropTypes.func,
  onLocalConnectionStateChanged: PropTypes.func,
  onLocalParticipantAttributesChanged: PropTypes.func,
  onLocalParticipantMetadataChanged: PropTypes.func,
  onLocalTrackStreamStateChanged: PropTypes.func,

  // Communication callbacks
  onUnreadMessageCount: PropTypes.func,
  onFlaggingChange: PropTypes.func,

  // Settings callbacks
  onSettingsChange: PropTypes.func,
};

ReactSession.defaultProps = {
  enabledComponents: config.defaultComponentState,
  video: config.video,
};

/**
 * Export the event emitter for external event listening
 * 
 * This allows components outside the SessionViewer to listen to events
 * without needing to pass callback props through multiple layers.
 * 
 * @example
 * import { sessionViewerEvents } from '@/shared/components/proctoring/SessionViewer';
 * 
 * // Listen to all sessionLoaded events
 * sessionViewerEvents.on('onSessionLoaded', (data) => {
 *   console.log('Session loaded:', data);
 * });
 * 
 * @example
 * // Listen to events for a specific session
 * sessionViewerEvents.onSession('session_123', 'onSessionLoaded', (data) => {
 *   console.log('My session loaded:', data);
 * });
 */
export { sessionViewerEvents } from '../../events/SessionViewerEventEmitter';